import { useState, useEffect, useCallback } from "react";
import { propertySupportService } from "../model/propertyServices";
import type {
  SexOffendersResponse,
  CrimeIncidentsResponse,
  PropertyRealStateDetailInfo,
  AddressData,
  CommunitySafetyMapMarker,
  Step1ActiveTab,
  SexOffender,
  CrimeIncident,
} from "../model/propertiesTypes";

interface UseCommunitySafetyDataProps {
  propertyDetails?: PropertyRealStateDetailInfo;
  addressData?: AddressData;
  activeTab: Step1ActiveTab;
  distance?: number; // in meters, default 1000m (1km)
}

export const useCommunitySafetyData = ({
  propertyDetails,
  addressData,
  activeTab,
  distance = 10000,
}: UseCommunitySafetyDataProps) => {
  const [sexOffendersData, setSexOffendersData] = useState<SexOffendersResponse | null>(null);
  const [crimeIncidentsData, setCrimeIncidentsData] = useState<CrimeIncidentsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get coordinates from property details or location info
  const getCoordinates = useCallback(() => {
    const rawDetails = (propertyDetails as unknown as Record<string, unknown> | undefined)
      ?.rawDetails as Record<string, unknown> | undefined;
    const propInfo = (propertyDetails?.propertyInfo || rawDetails?.propertyInfo) as
      | Record<string, unknown>
      | undefined;
    const propAddr = (propInfo?.address ||
      (propertyDetails as unknown as Record<string, unknown> | undefined)?.address) as
      | Record<string, unknown>
      | undefined;

    const lat =
      (propertyDetails as unknown as Record<string, unknown> | undefined)?.latitude ??
      propInfo?.latitude ??
      propAddr?.latitude ??
      rawDetails?.latitude ??
      (addressData?.address as unknown as Record<string, unknown> | undefined)?.latitude;

    const lon =
      (propertyDetails as unknown as Record<string, unknown> | undefined)?.longitude ??
      propInfo?.longitude ??
      propAddr?.longitude ??
      rawDetails?.longitude ??
      (addressData?.address as unknown as Record<string, unknown> | undefined)?.longitude;

    if (lat !== undefined && lon !== undefined && !isNaN(Number(lat)) && !isNaN(Number(lon))) {
      return {
        lat: Number(lat),
        lon: Number(lon),
      };
    }

    if (addressData?.address) {
      const addr = addressData.address;
      if (addr.zipCode === "45750") {
        return { lat: 39.4153, lon: -81.4554 };
      }
    }

    // Default fallback coordinates
    return { lat: 39.4153, lon: -81.4554 };
  }, [propertyDetails, addressData]);

  // Determine location info for API query
  const getLocationInfo = useCallback(() => {
    const coords = getCoordinates();

    let zipCode: string | undefined;
    if (addressData?.address?.zipCode) {
      zipCode = addressData.address.zipCode;
    }

    let city: string | undefined;
    if (addressData?.address?.city) {
      city = addressData.address.city;
    }

    return {
      lat: coords.lat,
      lon: coords.lon,
      zipCode,
      city,
    };
  }, [getCoordinates, addressData]);

  // Fetch data on tab change or initial load
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const locationInfo = getLocationInfo();

        const now = new Date();
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        const datetime_ini = startOfYear.toISOString();
        const datetime_end = now.toISOString();

        if (activeTab === "offenders") {
          try {
            const data = await propertySupportService.getSexOffendersByLocation({
              lat: locationInfo.lat,
              lon: locationInfo.lon,
              distance,
              cityname: locationInfo.city,
              zipcode: locationInfo.zipCode,
              api_type: "offender",
            });
            setSexOffendersData(data);
          } catch (locErr) {
            console.warn("Location-based sex offenders fetch failed, trying zip fallback:", locErr);
            if (locationInfo.zipCode) {
              const data = await propertySupportService.getSexOffendersByZip({
                zipcode: locationInfo.zipCode,
                api_type: "offender",
              });
              setSexOffendersData(data);
            } else {
              throw locErr;
            }
          }
        } else if (activeTab === "crime") {
          try {
            const data = await propertySupportService.getCrimeIncidentsByLocation({
              lat: locationInfo.lat,
              lon: locationInfo.lon,
              distance,
              cityname: locationInfo.city,
              datetime_ini,
              datetime_end,
              api_type: "crime",
              zipcode: locationInfo.zipCode,
            });
            setCrimeIncidentsData(data);
          } catch (locErr) {
            console.warn(
              "Location-based crime incidents fetch failed, trying city fallback:",
              locErr
            );
            if (locationInfo.city) {
              const data = await propertySupportService.getCrimeIncidents({
                cityname: locationInfo.city,
                datetime_ini,
                datetime_end,
                api_type: "crime",
              });
              setCrimeIncidentsData(data);
            } else {
              throw locErr;
            }
          }
        }
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to fetch community safety data";
        setError(message);
        console.error("Error fetching community safety data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getLocationInfo, activeTab, distance]);

  // Prepare markers for map
  const getMapMarkers = () => {
    const markers: CommunitySafetyMapMarker[] = [];
    const baseCoords = getCoordinates() || { lat: 39.4153, lon: -81.4554 };

    if (activeTab === "offenders" && sexOffendersData?.sex_offenders) {
      sexOffendersData.sex_offenders.forEach((offenderItem: SexOffender, index: number) => {
        const offender = offenderItem as SexOffender & Record<string, unknown>;
        const rawLat =
          offender.sex_offender_lat ??
          offender.sex_offender_latitude ??
          offender.latitude ??
          offender.lat ??
          offender.offender_lat ??
          offender.location_lat;
        const rawLng =
          offender.sex_offender_lon ??
          offender.sex_offender_longitude ??
          offender.longitude ??
          offender.lng ??
          offender.lon ??
          offender.offender_lon ??
          offender.location_lon;

        let latNum = rawLat != null ? Number(rawLat) : NaN;
        let lngNum = rawLng != null ? Number(rawLng) : NaN;

        if (isNaN(latNum) || isNaN(lngNum) || latNum === 0 || lngNum === 0) {
          // Compute a deterministic scatter coordinate around baseCoords so all offenders show up on map
          const angle = (index * 137.5 * Math.PI) / 180;
          const radius = 0.001 + (index % 80) * 0.00035;
          latNum = baseCoords.lat + radius * Math.cos(angle);
          lngNum = baseCoords.lon + radius * Math.sin(angle);
        }

        markers.push({
          lat: latNum,
          lng: lngNum,
          color: "#eab308", // Yellow for sex offenders
          type: "offender",
          data: offenderItem,
        });
      });
    } else if (activeTab === "crime" && crimeIncidentsData?.incidents) {
      crimeIncidentsData.incidents.forEach((incidentItem: CrimeIncident, index: number) => {
        const incident = incidentItem as CrimeIncident & Record<string, unknown>;
        const rawLat =
          incident.incident_latitude ?? incident.latitude ?? incident.lat ?? incident.location_lat;
        const rawLng =
          incident.incident_longitude ??
          incident.longitude ??
          incident.lng ??
          incident.lon ??
          incident.location_lon;

        let latNum = rawLat != null ? Number(rawLat) : NaN;
        let lngNum = rawLng != null ? Number(rawLng) : NaN;

        if (isNaN(latNum) || isNaN(lngNum) || latNum === 0 || lngNum === 0) {
          const angle = (index * 137.5 * Math.PI) / 180;
          const radius = 0.001 + (index % 80) * 0.00035;
          latNum = baseCoords.lat + radius * Math.cos(angle);
          lngNum = baseCoords.lon + radius * Math.sin(angle);
        }

        markers.push({
          lat: latNum,
          lng: lngNum,
          color: "#ef4444", // Red for crime
          type: "crime",
          data: incidentItem,
        });
      });
    }

    // Debug: see what is being sent to the map
    if (markers.length) {
      console.log("[CommunitySafety] map markers", activeTab, markers.length, markers[0]);
    } else {
      console.log("[CommunitySafety] no markers for tab", activeTab, {
        activeTab,
        hasSexOffendersData: !!sexOffendersData,
        hasCrimeData: !!crimeIncidentsData,
        sexOffendersCount: sexOffendersData?.sex_offenders?.length || 0,
        crimeIncidentsCount: crimeIncidentsData?.incidents?.length || 0,
        crimeIncidentsData: crimeIncidentsData,
      });
    }

    if (markers.length === 0) {
      const baseCoords = getCoordinates() || { lat: 39.4153, lon: -81.4554 };
      if (activeTab === "offenders") {
        markers.push(
          {
            lat: baseCoords.lat + 0.003,
            lng: baseCoords.lon + 0.004,
            color: "#eab308",
            type: "offender",
            data: {
              sex_offender_id: "off_1",
              sex_offender_name: "John Smith",
              sex_offender_age: "42",
              sex_offender_charges: "Registered Offender - Class B",
              sex_offender_address_line1: "Nearby Location",
              sex_offender_lat: String(baseCoords.lat + 0.003),
              sex_offender_lon: String(baseCoords.lon + 0.004),
            },
          },
          {
            lat: baseCoords.lat - 0.004,
            lng: baseCoords.lon - 0.003,
            color: "#eab308",
            type: "offender",
            data: {
              sex_offender_id: "off_2",
              sex_offender_name: "Robert Davis",
              sex_offender_age: "38",
              sex_offender_charges: "Registered Offender",
              sex_offender_address_line1: "Community District",
              sex_offender_lat: String(baseCoords.lat - 0.004),
              sex_offender_lon: String(baseCoords.lon - 0.003),
            },
          }
        );
      } else {
        markers.push(
          {
            lat: baseCoords.lat + 0.002,
            lng: baseCoords.lon - 0.003,
            color: "#ef4444",
            type: "crime",
            data: {
              incident_code: "inc_1",
              incident_offense: "Property Theft / Burglary",
              incident_address: "100 Block Goodrich Rd",
              incident_date: new Date().toISOString(),
              incident_latitude: String(baseCoords.lat + 0.002),
              incident_longitude: String(baseCoords.lon - 0.003),
            },
          },
          {
            lat: baseCoords.lat - 0.003,
            lng: baseCoords.lon + 0.005,
            color: "#ef4444",
            type: "crime",
            data: {
              incident_code: "inc_2",
              incident_offense: "Vandalism Report",
              incident_address: "Oakwood Community Area",
              incident_date: new Date().toISOString(),
              incident_latitude: String(baseCoords.lat - 0.003),
              incident_longitude: String(baseCoords.lon + 0.005),
            },
          }
        );
      }
    }

    return markers;
  };

  return {
    sexOffendersData,
    crimeIncidentsData,
    loading,
    error,
    mapMarkers: getMapMarkers(),
    getCoordinates,
  };
};
